import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Home.css';

const FAQ_SCHEMA = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What does ATZ Solution LLC do?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'ATZ Solution LLC is a third-party federal contracting consulting firm that helps businesses register in SAM, obtain small-business certifications, find daily contract opportunities, write and submit bids, and access subcontracting work in the federal marketplace.',
      },
    },
    {
      '@type': 'Question',
      name: 'Is ATZ Solution LLC a government agency?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'No. ATZ Solution LLC is not a government agency. It is a third-party consulting firm that provides assistance with SAM.gov processing and filing, small-business certifications and registrations, and other government contractor services.',
      },
    },
    {
      '@type': 'Question',
      name: 'Where is ATZ Solution LLC located?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'ATZ Solution LLC is based at 301 S McDowell Street, Suite 125, Charlotte, NC 28204.',
      },
    },
    {
      '@type': 'Question',
      name: 'What services does ATZ Solution LLC offer?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'ATZ Solution LLC offers SAM registration and optimization, small-business certifications, capability statement development, federal landing pages, email outreach, daily federal contract opportunity leads, full bid writing and submission, and subcontracting access.',
      },
    },
  ],
};

export default function Home() {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify(FAQ_SCHEMA);
    document.head.appendChild(script);
    return () => {
      document.head.removeChild(script);
    };
  }, []);

  return (
    <main id="top" data-hero="split">

      {/* HERO */}
      <section className="hero">

        {/* Split */}
        <div className="hero-block hero-split" data-screen-label="Hero · Split" style={{ padding: '54px 0px 24px' }}>
          <div className="wrap-wide">
            <div className="grid">
              <div className="copy">
                <span className="eyebrow" data-hero-eyebrow>SAM.gov · SBA Certifications · Federal Contracting</span>
                <h1 data-hero-h>Win federal contracts without navigating the bureaucracy alone.</h1>
                <p className="sub" data-hero-sub>We are the expert partner for SAM.gov registration, SBA certification, and federal compliance managing the entire filing process so your business is contract-ready and audit-proof.</p>
                <div className="hero-actions">
                  <a className="btn btn-primary" href="/contact-us/">Start Your Registration <span className="arw">→</span></a>
                  <a className="btn btn-red" href="/contact-us/">Speak with a Specialist</a>
                </div>
                <div className="hero-rating">
                  <span className="stars"><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg></span>
                  <span className="rtext"><b>4.9/5</b> from 900+ verified reviews</span>
                  <span className="dot"></span>
                  <span className="mins"><svg className="ico"><use href="#i-clock" /></svg> Takes less than 10 minutes to start</span>
                </div>
              </div>
              <div className="media">
                <img className="ph" src="/images/capitol-flag.jpg" alt="U.S. Capitol — formal wide shot" />
                <div className="stamp">
                  <div className="n">5+ yrs</div>
                  <div className="l">of federal filing & certification experience</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Centered */}
        <div className="hero-block hero-centered" data-screen-label="Hero · Centered">
          <div className="bg"><img className="ph" src="/images/capitol-dome-flag.jpg" alt="Capitol dome — dramatic low angle, dusk" /></div>
          <div className="scrim"></div>
          <div className="inner">
            <div className="wrap">
              <span className="eyebrow center" data-hero-eyebrow>SAM.gov · SBA Certifications · Federal Contracting</span>
              <h2 data-hero-h>Win federal contracts without navigating the bureaucracy alone.</h2>
              <p className="sub" data-hero-sub>We are the expert partner for SAM.gov registration, SBA certification, and federal compliance — managing the entire filing process so your business is contract-ready and audit-proof.</p>
              <div className="hero-actions">
                <a className="btn btn-light" href="/contact-us/">Start Your Registration <span className="arw">→</span></a>
                <a className="btn btn-red" href="/contact-us/">Speak with a Specialist</a>
              </div>
              <div className="hero-rating">
                <span className="stars"><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg></span>
                <span className="rtext"><b>4.9/5</b> from 900+ verified reviews</span>
                <span className="dot"></span>
                <span className="mins"><svg className="ico"><use href="#i-clock" /></svg> Takes less than 10 minutes to start</span>
              </div>
              <div className="scrolldot">Scroll to explore our services</div>
            </div>
          </div>
        </div>

        {/* Framed */}
        <div className="hero-block hero-framed" data-screen-label="Hero · Framed">
          <div className="wrap-wide">
            <div className="frame">
              <div className="frame-in">
                <div className="toprow">
                  <div className="brand">
                    <span className="seal-logo"><img src="/atz-logo-white.png" alt="ATZ Solution" /></span>
                    <span className="wm"><b style={{ fontFamily: 'var(--font-display)', fontWeight: '700', fontSize: '20px', color: 'var(--navy)' }}>ATZ Solution</b></span>
                  </div>
                  <div className="lab">Est. 2015 · Charlotte, NC</div>
                </div>
                <div className="grid">
                  <div className="copy">
                    <span className="eyebrow" data-hero-eyebrow>SAM.gov · SBA Certifications · Federal Contracting</span>
                    <h2 data-hero-h>Win federal contracts without navigating the bureaucracy alone.</h2>
                    <p className="sub" data-hero-sub>We are the expert partner for SAM.gov registration, SBA certification, and federal compliance — managing the entire filing process so your business is contract-ready and audit-proof.</p>
                    <div className="hero-actions">
                      <a className="btn btn-primary" href="/contact-us/">Start Your Registration <span className="arw">→</span></a>
                      <a className="btn btn-red" href="/contact-us/">Speak with a Specialist</a>
                    </div>
                    <div className="hero-rating">
                      <span className="stars"><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg></span>
                      <span className="rtext"><b>4.9/5</b> from 900+ reviews</span>
                      <span className="dot"></span>
                      <span className="mins"><svg className="ico"><use href="#i-clock" /></svg> 10 minutes to start</span>
                    </div>
                  </div>
                  <div className="media">
                    <img className="ph" src="/images/documents-handoff.jpg" alt="Professionals reviewing official documents" />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

      </section>

      {/* TRUST BAR */}
      <section className="trust">
        <div className="wrap-wide">
          <div className="lab">Trusted by thousands of businesses across every major federal program</div>
          <div className="marks">
            <div className="m">SAM.gov</div>
            <div className="m">SBA 8(a)</div>
            <div className="m">WOSB / EDWOSB</div>
            <div className="m">HUBZone</div>
            <div className="m">SDVOSB</div>
            <div className="m">GSA Schedule</div>
          </div>
        </div>
      </section>

      {/* STATS */}
      <section className="stats" id="results">
        <div className="bg"><img className="ph" src="/images/capitol-facade.jpg" alt="U.S. Capitol at dusk — wide cinematic" /></div>
        <div className="scrim" style={{ backgroundSize: 'contain', height: '279px', backgroundPosition: 'center center' }}></div>
        <div className="wrap-wide">
          <div className="sec-head center">
            <span className="eyebrow center">Join thousands of businesses</span>
            <h2>5+ Years in the Federal Marketplace.</h2>
          </div>
          <div className="stat-bar">
            <div className="stat">
              <span className="si"><svg className="ico"><use href="#i-landmark" /></svg></span>
              <div className="st-txt">
                <div className="n">2,400<em>+</em></div>
                <div className="l">Businesses registered & certified</div>
              </div>
            </div>
            <div className="stat">
              <span className="si"><svg className="ico"><use href="#i-shield" /></svg></span>
              <div className="st-txt">
                <div className="n">98<em>%</em></div>
                <div className="l">First-time approval rate</div>
              </div>
            </div>
            <div className="stat">
              <span className="si"><svg className="ico"><use href="#i-trophy" /></svg></span>
              <div className="st-txt">
                <div className="n">$3.99<em>B+</em></div>
                <div className="l">Contract eligibility unlocked</div>
              </div>
            </div>
            <div className="stat">
              <span className="si"><svg className="ico"><use href="#i-clock" /></svg></span>
              <div className="st-txt">
                <div className="n">25<em>+</em></div>
                <div className="l">Expert consultants on our team</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="services sec-pad" id="services" style={{ padding: '46px 0px 54px' }}>
        <div className="wrap-wide">
          <div className="sec-head">
            <span className="kicker-num">01 — What we do</span>
            <h2>Everything you need to win federal work, in one place.</h2>
            <p className="lead">From SAM.gov registration through proposal development and award, our team manages every stage of the federal contracting process.</p>
          </div>
          <div className="bento">
            <article className="svc6 feature">
              <div className="feat-media"><img className="ph" src="/images/specialist-sam-review.jpg" alt="Specialist reviewing a SAM.gov filing" /></div>
              <div className="feat-body">
                <span className="iconwrap"><svg className="ico"><use href="#i-doc" /></svg></span>
                <span className="tag">Most requested</span>
                <h3><Link className="card-link" to="/sam-registration/">SAM.gov Registration</Link></h3>
                <p>Complete entity registration and UEI acquisition that gets your business active, validated, and visible to every federal buyer — prepared and submitted on your behalf, correct the first time.</p>
                <span className="more">Learn about SAM.gov Registration <span className="arw">→</span></span>
              </div>
            </article>
            <article className="svc6">
              <span className="iconwrap"><svg className="ico"><use href="#i-refresh" /></svg></span>
              <h3><Link className="card-link" to="/contact-us/">Renewals & Compliance</Link></h3>
              <p>Annual renewals, reports & certificates, and active monitoring so your registration never lapses.

</p>
              <span className="more">Learn more <span className="arw">→</span></span>
            </article>
            <article className="svc6">
              <span className="iconwrap"><svg className="ico"><use href="#i-award" /></svg></span>
              <h3><a className="card-link" href="#certifications">SBA Certifications</a></h3>
              <p>8(a), WOSB/EDWOSB, HUBZone, and SDVOSB applications prepared and shepherded to approval.</p>
              <span className="more">Learn more <span className="arw">→</span></span>
            </article>
            <article className="svc6">
              <span className="iconwrap"><svg className="ico"><use href="#i-clipboard" /></svg></span>
              <h3><Link className="card-link" to="/contact-us/">Capability Statement</Link></h3>
              <p>A clear, federal-ready document presenting your capabilities, NAICS codes, and past performance to contracting officers and prime contractors.</p>
              <span className="more">Learn more <span className="arw">→</span></span>
            </article>
            <article className="svc6">
              <span className="iconwrap"><svg className="ico"><use href="#i-target" /></svg></span>
              <h3><Link className="card-link" to="/contact-us/">Consulting & Strategy</Link></h3>
              <p>Direct guidance on NAICS positioning, set-aside targeting, and the specific steps your business needs to compete and win.</p>
              <span className="more">Learn more <span className="arw">→</span></span>
            </article>
            <article className="svc6">
              <span className="iconwrap"><svg className="ico"><use href="#i-megaphone" /></svg></span>
              <h3><Link className="card-link" to="/contact-us/">Federal Outreach & Representation</Link></h3>
              <p>ATZ represents your business in agency outreach and connects you to subcontract opportunities through our prime contractor portfolio.</p>
              <span className="more">Learn more <span className="arw">→</span></span>
            </article>
          </div>
        </div>
      </section>

      {/* REVIEWS */}
      <section className="reviews sec-pad-sm" id="reviews">
        <div className="wrap-wide">
          <div className="reviews-head">
            <div>
              <span className="eyebrow">What our clients say</span>
              <h2>Trusted by business owners nationwide.</h2>
            </div>
            <div className="reviews-score">
              <span className="stars"><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg></span>
              <div className="rs-txt"><b>4.9 / 5</b> from 900+ verified reviews</div>
            </div>
          </div>
          <div className="review-grid">
            <figure className="review">
              <span className="stars"><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg></span>
              <blockquote>“We stalled on SAM.gov twice on our own. ATZ had us active in under two weeks and guided our 8(a) start to finish.”</blockquote>
              <figcaption><span className="av">DM</span><span className="rv-who"><span className="nm">Denise Morales</span><span className="rl">Meridian Facilities Group</span></span></figcaption>
            </figure>
            <figure className="review">
              <span className="stars"><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg></span>
              <blockquote>“Clear, fast, and accurate. Our HUBZone certification came through on the first submission — no back-and-forth.”</blockquote>
              <figcaption><span className="av">RT</span><span className="rv-who"><span className="nm">Raymond Tran</span><span className="rl">Castlewood Logistics</span></span></figcaption>
            </figure>
            <figure className="review">
              <span className="stars"><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg></span>
              <blockquote>“They handle every renewal before I even think about it. It's like having a compliance department on retainer.”</blockquote>
              <figcaption><span className="av">SK</span><span className="rv-who"><span className="nm">Sarah Keller</span><span className="rl">Northbridge Technical Services</span></span></figcaption>
            </figure>
          </div>
        </div>
      </section>

      {/* CERTIFICATIONS */}
      <section className="certs sec-pad" id="certifications">
        <div className="wrap-wide">
          <div className="sec-head">
            <span className="kicker-num">02 — SBA set-aside programs</span>
            <h2>The certifications that put your business first in line.</h2>
            <p className="lead">Federal agencies set aside a share of every contract dollar for certified small businesses. We determine exactly what you qualify for then get you certified.</p>
          </div>
          <div className="cert-grid">
            <article className="cert">
              <span className="badge"><svg className="ico"><use href="#i-star" /></svg></span>
              <h3>WOSB / EDWOSB</h3>
              <div className="sub">Women-Owned</div>
              <p>Women-Owned & Economically Disadvantaged Small Business programs.</p>
              <span className="lk">Check eligibility <svg className="ico" style={{ width: '15px', height: '15px' }}><use href="#i-arrow" /></svg></span>
            </article>
            <article className="cert">
              <span className="badge"><svg className="ico"><use href="#i-shield" /></svg></span>
              <h3>VOSB / SDVOSB</h3>
              <div className="sub">Veteran-Owned</div>
              <p>Veteran-Owned and Service-Disabled Veteran-Owned Small Business.</p>
              <span className="lk">Check eligibility <svg className="ico" style={{ width: '15px', height: '15px' }}><use href="#i-arrow" /></svg></span>
            </article>
            <article className="cert">
              <span className="badge"><svg className="ico"><use href="#i-mappin" /></svg></span>
              <h3>HUBZone</h3>
              <div className="sub">Underutilized Zone</div>
              <p>Historically Underutilized Business Zone program certification.</p>
              <span className="lk">Check eligibility <svg className="ico" style={{ width: '15px', height: '15px' }}><use href="#i-arrow" /></svg></span>
            </article>
            <article className="cert">
              <span className="badge"><svg className="ico"><use href="#i-award" /></svg></span>
              <h3>8(a) Program</h3>
              <div className="sub">Business Development</div>
              <p>SBA's nine-year Business Development program for disadvantaged firms.</p>
              <span className="lk">Check eligibility <svg className="ico" style={{ width: '15px', height: '15px' }}><use href="#i-arrow" /></svg></span>
            </article>
          </div>
        </div>
      </section>

      {/* WHY US */}
      <section className="whyus sec-pad" id="why" style={{ padding: '37px 0px 50px' }}>
        <div className="wrap-wide">
          <div className="why-layout">
            <div className="why-media">
              <img className="ph" src="/images/advisor-client.jpg" alt="Account executive with a client — warm, professional" style={{ height: '659px', width: '566px' }} />
              <div className="why-badge">
                <span className="stars"><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg><svg className="ico"><use href="#i-star" /></svg></span>
                <div className="wb-t">4.9/5 average rating</div>
                <div className="wb-s">from 900+ verified clients</div>
              </div>
            </div>
            <div className="why-content">
              <span className="kicker-num">Why choose ATZ</span>
              <h2>The right partner makes federal contracting simple.</h2>
              <p className="lead">Federal agencies set aside a portion of every contract dollar for certified small businesses. ATZ Solution determines what you qualify for and manages the entire filing.</p>
              <div className="why-list">
                <div className="why">
                  <span className="iconwrap"><svg className="ico"><use href="#i-shield" /></svg></span>
                  <div>
                    <h3>Trusted Experts</h3>
                    <p>Five-plus years specializing exclusively in federal registration and certification.</p>
                  </div>
                </div>
                <div className="why">
                  <span className="iconwrap"><svg className="ico"><use href="#i-star" /></svg></span>
                  <div>
                    <h3>Excellent Reputation</h3>
                    <p>Thousands of businesses registered, certified, and kept contract-ready.</p>
                  </div>
                </div>
                <div className="why">
                  <span className="iconwrap"><svg className="ico"><use href="#i-target" /></svg></span>
                  <div>
                    <h3>Fast, Friendly & Accurate</h3>
                    <p>Done right the first time, with a personal specialist by your side.</p>
                  </div>
                </div>
              </div>
              <figure className="why-quote">
                <p>“We had tried SAM.gov ourselves twice and stalled both times. ATZ had us active in under two weeks and walked us through our 8(a) application start to finish.”</p>
                <figcaption><span className="av">DM</span><span><span className="nm">Denise Morales</span><span className="rl">Owner, Meridian Facilities Group</span></span></figcaption>
              </figure>
            </div>
          </div>
        </div>
      </section>

      {/* JOURNEY */}
      <section className="journey sec-pad" id="process">
        <div className="wrap-wide">
          <div className="sec-head center">
            <span className="eyebrow center">Our process · Simple, proven, government-ready</span>
            <h2>Three steps from paperwork to your first federal contract.</h2>
          </div>
          <div className="journey-grid">
            <div className="jstep">
              <span className="seal-lg"><span className="sn">1</span><svg className="ico"><use href="#i-landmark" /></svg></span>
              <h3>Register</h3>
              <p>We get your business listed and active in SAM.gov the federal government's official vendor system.</p>
            </div>
            <div className="jarrow" aria-hidden="true"><svg viewBox="0 0 74 44"><path className="ad-line" d="M4 22 L60 22" /><path className="ad-head" d="M49 13 L62 22 L49 31" /></svg></div>
            <div className="jstep">
              <span className="seal-lg"><span className="sn">2</span><svg className="ico"><use href="#i-award" /></svg></span>
              <h3>Certify</h3>
              <p>We qualify you for the SBA certifications that set your business apart and open set-aside contracts.</p>
            </div>
            <div className="jarrow" aria-hidden="true"><svg viewBox="0 0 74 44"><path className="ad-line" d="M4 22 L60 22" /><path className="ad-head" d="M49 13 L62 22 L49 31" /></svg></div>
            <div className="jstep">
              <span className="seal-lg"><span className="sn">3</span><svg className="ico"><use href="#i-trophy" /></svg></span>
              <h3>Win</h3>
              <p>You bid on federal contracts with confidence fully compliant and ready to compete.</p>
            </div>
          </div>
        </div>
      </section>

      {/* PORTFOLIO */}
      <section className="portfolio sec-pad" id="portfolio">
        <div className="wrap-wide">
          <div className="sec-head">
            <span className="kicker-num">Our portfolio</span>
            <h2>A track record you can point to.</h2>
            <p className="lead">Real registrations, certifications, and set-aside awards we've helped clients secure across federal agencies.</p>
          </div>
          <div className="port-grid">
            <article className="port" style={{ cursor: 'pointer' }}>
              <div className="port-media"><img className="ph" src="/images/team-meeting.jpg" alt="Project / agency photo" /><span className="port-tag">Proposal Support</span></div>
              <div className="port-body">
                <div className="port-agency">Dept. of Veterans Affairs</div>
                <h3>Government mold remediation contract</h3>
                <div className="port-meta"><span className="pv">$70K</span><span className="pd">Awarded 2024</span></div>
              </div>
            </article>
            <article className="port">
              <div className="port-media"><img className="ph" src="/images/officials-conference.jpg" alt="Project / agency photo" /><span className="port-tag">8(a) Program</span></div>
              <div className="port-body">
                <div className="port-agency">General Services Administration</div>
                <h3>IT services & support vehicle</h3>
                <div className="port-meta"><span className="pv">$1.8M</span><span className="pd">Awarded 2024</span></div>
              </div>
            </article>
            <article className="port">
              <div className="port-media"><img className="ph" src="/images/advisor-flag.jpg" alt="Project / agency photo" /><span className="port-tag">HUBZone</span></div>
              <div className="port-body">
                <div className="port-agency">U.S. Army Corps of Engineers</div>
                <h3>Site construction & logistics</h3>
                <div className="port-meta"><span className="pv">$3.1M</span><span className="pd">Awarded 2024</span></div>
              </div>
            </article>
          </div>
          <div className="port-foot">
            <span>Representative engagements — outcomes vary by business and program eligibility.</span>
            <Link className="more" to="/portfolio/">View full portfolio <span className="arw">→</span></Link>
          </div>
        </div>
      </section>

      {/* PRICING */}
      <section className="pricing sec-pad" id="pricing" style={{ padding: '80px 0px 84px' }}>
        <div className="wrap-wide">
          <div className="sec-head center">
            <span className="eyebrow center">Our programs</span>
            <h2>Choose the program that matches where you are.</h2>
            <p className="lead">Three guided programs take you from your first federal registration to full-scale contract pursuit. Every program is tailored after a free consultation — reach out and we'll map the right path for your business.</p>
          </div>
          <div className="price-grid">
            <article className="plan">
              <h3 className="pname">Subcontractor Partnership Program</h3>
              <div className="pdesc">For businesses ready to start winning work as a subcontractor under federal prime contractors.</div>
              <ul>
                <li><svg className="ico"><use href="#i-shield" /></svg> SAM.gov registration & UEI setup</li>
                <li><svg className="ico"><use href="#i-shield" /></svg> NAICS & size-standard positioning</li>
                <li><svg className="ico"><use href="#i-shield" /></svg> Capability statement to share with primes</li>
                <li><svg className="ico"><use href="#i-shield" /></svg> Guidance on finding subcontract opportunities</li>
              </ul>
              <a className="btn btn-ghost" href="/contact-us/">Contact Now</a>
            </article>
            <article className="plan featured">
              <span className="ribbon">Most Popular</span>
              <h3 className="pname">Federal Growth Program</h3>
              <div className="pdesc">For established businesses ready to compete for set-aside prime contracts of their own.</div>
              <ul>
                <li><svg className="ico"><use href="#i-shield" /></svg> Everything in the Partnership Program</li>
                <li><svg className="ico"><use href="#i-shield" /></svg> SBA certification (8(a), WOSB, HUBZone, SDVOSB)</li>
                <li><svg className="ico"><use href="#i-shield" /></svg> Annual renewals & compliance monitoring</li>
                <li><svg className="ico"><use href="#i-shield" /></svg> Bid & proposal strategy support</li>
                <li><svg className="ico"><use href="#i-shield" /></svg> Priority specialist support</li>
              </ul>
              <a className="btn btn-primary" href="/contact-us/">Contact Now</a>
            </article>
            <article className="plan">
              <h3 className="pname">Federal Expansion Program</h3>
              <div className="pdesc">For growing firms ready to scale federal revenue across agencies and contract vehicles.</div>
              <ul>
                <li><svg className="ico"><use href="#i-shield" /></svg> Everything in the Growth Program</li>
                <li><svg className="ico"><use href="#i-shield" /></svg> GSA Schedule readiness & onboarding</li>
                <li><svg className="ico"><use href="#i-shield" /></svg> Multi-certification & recertification management</li>
                <li><svg className="ico"><use href="#i-shield" /></svg> Contract marketing & agency outreach</li>
                <li><svg className="ico"><use href="#i-shield" /></svg> Dedicated growth strategist</li>
              </ul>
              <a className="btn btn-ghost" href="/contact-us/">Contact Now</a>
            </article>
          </div>
          <p className="price-note">Each program is scoped and quoted individually after a free, no-obligation consultation.</p>
        </div>
      </section>

      {/* FAQ */}
      <section className="faq sec-pad" id="faq" style={{ padding: '45px 0px 80px' }}>
        <div className="wrap-wide">
          <div className="faq-grid">
            <div className="sec-head">
              <span className="kicker-num">04 — Common questions</span>
              <h2>Clear answers, before you ask.</h2>
              <p className="lead">Transparency is part of doing this correctly. If your question isn't here, a specialist will answer it on your first call.</p>
            </div>
            <div className="faq-list">
              <details className="qa" open>
                <summary><h3>Are you affiliated with the government or SAM.gov?</h3></summary>
                <div className="ans">No. ATZ Solution is an independent, privately owned consulting firm. We are not affiliated with, endorsed by, or sponsored by the U.S. government, SAM.gov, the SBA, or any federal agency. We are a professional services firm you hire to prepare and manage your filings.</div>
              </details>
              <details className="qa">
                <summary><h3>SAM.gov registration is free. Why use ATZ?</h3></summary>
                <div className="ans">You can register directly at SAM.gov at no cost. Business owners hire us for the same reason they hire an accountant for free tax forms: accuracy, speed, and risk. A single error can delay activation by weeks or trigger a rejection. We handle the complexity correctly the first time and stand alongside you through every reviewer interaction.</div>
              </details>
              <details className="qa">
                <summary><h3>How long does registration and certification take?</h3></summary>
                <div className="ans">A complete SAM.gov registration is typically active within one to three weeks once documentation is in order. SBA certifications such as 8(a) or HUBZone vary by program and review queue, generally ranging from several weeks to a few months. We give you a realistic timeline up front and manage it actively.</div>
              </details>
              <details className="qa">
                <summary><h3>Which certifications can you help with?</h3></summary>
                <div className="ans">We support the full range of federal small-business programs: 8(a) Business Development, Woman-Owned (WOSB/EDWOSB), HUBZone, and Veteran-Owned and Service-Disabled Veteran-Owned (VOSB/SDVOSB), along with GSA Schedule readiness and NAICS positioning.</div>
              </details>
              <details className="qa">
                <summary><h3>What if my registration was rejected or expired before?</h3></summary>
                <div className="ans">That is one of the most common reasons clients come to us. We diagnose exactly what went wrong, correct the record, and re-establish your active status — then put renewal monitoring in place so it never lapses again.</div>
              </details>
              <details className="qa">
                <summary><h3>How are your fees structured?</h3></summary>
                <div className="ans">Our fees are flat and disclosed in writing before any work begins — they cover professional advisory and filing services only, never government fees (federal registration itself is free). After your consultation you receive a clear scope and price with no surprises.</div>
              </details>
            </div>
          </div>
        </div>
      </section>

      {/* KNOWLEDGE HUB */}
      <section className="khub sec-pad" id="resources">
        <div className="wrap-wide">
          <div className="sec-head">
            <span className="kicker-num">05 — Knowledge & Growth Hub</span>
            <h2>Guidance for every step of federal contracting.</h2>
            <p className="lead">Plain-English guides from our specialists on registration, certification, and winning your first bid.</p>
          </div>
          <div className="khub-grid">
            <article className="post">
              <img className="ph" src="/images/hubzone-map.jpg" alt="Article image — HUBZone map" />
              <div className="body">
                <span className="cat">Certifications</span>
                <h3>HUBZone Certification: A Guide for Small Businesses</h3>
                <Link className="more" to="/blogs/">Read more <span className="arw">→</span></Link>
              </div>
            </article>
            <article className="post">
              <img className="ph" src="/images/proposal-desk.jpg" alt="Article image — proposal desk" />
              <div className="body">
                <span className="cat">Winning Work</span>
                <h3>Proposal Writing Tips for Winning Federal Bids</h3>
                <Link className="more" to="/blogs/">Read more <span className="arw">→</span></Link>
              </div>
            </article>
            <article className="post">
              <img className="ph" src="/images/veteran-owner.jpg" alt="Article image — veteran business owner" />
              <div className="body">
                <span className="cat">Certifications</span>
                <h3>SDVOSB Certification: Requirements & Benefits Explained</h3>
                <Link className="more" to="/blogs/">Read more <span className="arw">→</span></Link>
              </div>
            </article>
          </div>
        </div>
      </section>

      {/* FINAL CTA */}
      <section className="finalcta" id="contact">
        <div className="wrap">
          <span className="starrow center" style={{ marginBottom: '10px' }}><svg viewBox="0 0 24 24"><use href="#i-star" /></svg><svg viewBox="0 0 24 24"><use href="#i-star" /></svg><svg viewBox="0 0 24 24"><use href="#i-star" /></svg></span>
          <img className="brand-logo" style={{ height: '36px', margin: '0 auto 6px' }} src="/atz-logo-white.png" alt="ATZ Solution" />
          <span className="eyebrow">Ready when you are</span>
          <h2>Become contract-ready with a firm that handles the hard part.</h2>
          <p>Start your registration today, or speak with a specialist who can map your exact path into federal contracting. No obligation, no jargon — just a clear next step.</p>
          <div className="hero-actions">
            <a className="btn btn-light" href="/contact-us/">Start Your Registration <span className="arw">→</span></a>
            <a className="btn btn-outline-light" href="/contact-us/">Schedule a Consultation</a>
          </div>
          <div className="call">Prefer to talk now? Call <b>(704) 459-8642</b> · Mon–Fri 9:00–6:00 ET</div>
        </div>
      </section>

    </main>
  );
}
